import type { CreateDocumentRequest } from '@palaccount/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCheck, FileDown, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { downloadDocument } from '@/features/documents/api';
import { downloadListXlsx, type ListExportColumn } from '@/features/reports/export';
import { localizeServerCause } from '@/lib/server-messages';

import type { GrantablePermission } from '@palaccount/contracts';

/**
 * Wave 3B — the three acts every list offers over the rows ticked in table
 * mode. Each runs the rows ONE AT A TIME through the ordinary route: there
 * is no bulk endpoint and none wanted (`useDeleteDeliveries` gives the
 * reason — each document is refused on its own terms), the walk stops at
 * the first refusal and names it, and whatever posted before it stays
 * posted. The caches are dropped once at the end, whatever happened.
 */

/** Post the ticked DRAFTS, in order; the first refusal stops the walk. */
export function BulkPostButton<Row extends { id: string; code: string; status: string }>({
  rows,
  post,
  permission,
  reasonKey,
  invalidate,
  clear,
}: {
  rows: readonly Row[];
  post: (row: Row) => Promise<unknown>;
  permission: GrantablePermission;
  reasonKey: string;
  /** The query keys to drop once the walk ends. */
  invalidate: readonly (readonly string[])[];
  clear: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const drafts = rows.filter((row) => row.status === 'draft');
  if (drafts.length === 0) return null;
  const run = async () => {
    setBusy(true);
    setNote(null);
    let posted = 0;
    try {
      for (const row of drafts) {
        await post(row);
        posted += 1;
      }
      setNote(t('doclist.bulkPosted', { n: posted }));
      clear();
    } catch (thrown) {
      setNote(
        t('doclist.bulkStopped', {
          n: posted,
          code: drafts[posted]?.code ?? '',
          reason: localizeServerCause(t, thrown, 'common.loadFailed'),
        }),
      );
    } finally {
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
      setBusy(false);
    }
  };
  return (
    <>
      <Gated permission={permission} reasonKey={reasonKey}>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void run()}>
          {busy ? <Loader2 className="animate-spin" aria-hidden /> : <CheckCheck aria-hidden />}
          {t('doclist.bulkPost', { n: drafts.length })}
        </Button>
      </Gated>
      {note ? (
        <span role="status" className="text-xs text-muted-foreground">
          {note}
        </span>
      ) : null}
    </>
  );
}

/** One PDF per ticked row, each through the worker, one after another. */
export function BulkPdfButton({ requests }: { requests: readonly CreateDocumentRequest[] }) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'working' | 'failed'>('idle');
  const run = async () => {
    setState('working');
    try {
      for (const request of requests) await downloadDocument(request);
      setState('idle');
    } catch {
      setState('failed');
    }
  };
  return (
    <Button type="button" size="sm" variant="outline" disabled={state === 'working'} onClick={() => void run()}>
      {state === 'working' ? <Loader2 className="animate-spin" aria-hidden /> : <FileDown aria-hidden />}
      {state === 'failed' ? t('doclist.pdfFailed') : t('doclist.bulkPdf', { n: requests.length })}
    </Button>
  );
}

/** The ticked rows as a sheet — the same columns the page's Export writes. */
export function BulkExportButton<Row>({
  filename,
  columns,
  rows,
}: {
  filename: string;
  columns: readonly ListExportColumn<Row>[];
  rows: readonly Row[];
}) {
  const { t, i18n } = useTranslation();
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={() => downloadListXlsx(filename, columns, rows, i18n.dir() === 'rtl')}
    >
      <FileSpreadsheet aria-hidden /> {t('doclist.bulkExport', { n: rows.length })}
    </Button>
  );
}
